import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { Card } from '../ui'
import { Users, GripVertical, AlertCircle, CheckCircle2, Wand2, Trash2 } from 'lucide-react'
import { soundFx } from '../../lib/soundEffects'

export const PouleIndeling = ({ tournamentId, divisions, poules, teams, setTeams }) => {
    const { t } = useTranslation()
    const [draggedTeam, setDraggedTeam] = useState(null)

    // Group teams by poule_id (null for unassigned)
    const getTeamsByPoule = (pouleId) => {
        return teams.filter(t => t.poule_id === pouleId)
    }

    const unassignedTeams = getTeamsByPoule(null)

    const handleDragStart = (e, team) => {
        setDraggedTeam(team)
        e.dataTransfer.effectAllowed = 'move'
        // Optional: set drag image or data
    }

    const handleDragOver = (e) => {
        e.preventDefault() // Necessary to allow dropping
        e.dataTransfer.dropEffect = 'move'
    }

    const handleDrop = async (e, targetPouleId) => {
        e.preventDefault()

        if (!draggedTeam) return
        if (draggedTeam.poule_id === targetPouleId) return // No change

        // 1. Optimistic Update
        const updatedTeam = { ...draggedTeam, poule_id: targetPouleId }
        const previousTeams = [...teams]

        setTeams(teams.map(t => t.id === draggedTeam.id ? updatedTeam : t))
        setDraggedTeam(null)

        try {
            // 2. Database Update
            const { error } = await supabase
                .from('teams')
                .update({ poule_id: targetPouleId })
                .eq('id', draggedTeam.id)

            if (error) throw error
        } catch (err) {
            console.error('Move failed:', err)
            alert('Verplaatsen mislukt: ' + err.message)
            setTeams(previousTeams) // Rollback
        }
    }

    const handleAutoAssign = async () => {
        if (poules.length === 0) return alert('Maak eerst poules aan!')
        if (unassignedTeams.length === 0) return alert('Alle teams zijn al ingedeeld.')
        if (!confirm(`Wil je ${unassignedTeams.length} teams automatisch verdelen over ${poules.length} poules?`)) return

        const teamsToAssign = [...unassignedTeams].sort(() => Math.random() - 0.5) // Shuffle
        const newAssignments = []

        // Distribution logic: Round Robin
        teamsToAssign.forEach((team, index) => {
            const pouleIndex = index % poules.length
            newAssignments.push({ ...team, poule_id: poules[pouleIndex].id })
        })

        // Optimistic UI update
        const previousTeams = [...teams]
        const updatedTeamsList = teams.map(t => {
            const assignment = newAssignments.find(a => a.id === t.id)
            return assignment || t
        })
        setTeams(updatedTeamsList)

        try {
            // Batch update using upsert or multiple updates (Supabase doesn't have simple bulk update with different values easily without RPC, but we can iterate or use upsert if we have all fields. 
            // Better: loop update for now or use upsert with exact fields)

            const { error } = await supabase
                .from('teams')
                .upsert(newAssignments.map(t => ({
                    id: t.id,
                    tournament_id: tournamentId,
                    name: t.name,
                    logo_url: t.logo_url,
                    poule_id: t.poule_id
                })))

            if (error) throw error
            soundFx.play('success')
        } catch (err) {
            console.error('Auto assign failed:', err)
            alert('Indelen mislukt: ' + err.message)
            setTeams(previousTeams)
        }
    }

    const handleClearAssignment = async () => {
        const assignedTeams = teams.filter(t => t.poule_id)
        if (assignedTeams.length === 0) return
        if (!confirm(t('common.confirm_wipe_assignments'))) return

        const previousTeams = [...teams]
        setTeams(teams.map(t => ({ ...t, poule_id: null })))

        try {
            const { error } = await supabase
                .from('teams')
                .update({ poule_id: null })
                .eq('tournament_id', tournamentId)
                .not('poule_id', 'is', null)

            if (error) throw error
            soundFx.play('trash')
        } catch (err) {
            alert('Wissen mislukt: ' + err.message)
            setTeams(previousTeams)
        }
    }

    return (
        <div className="h-[calc(100vh-200px)] flex flex-col">
            <div className="flex justify-between items-center mb-4 px-1">
                <p className="text-gray-500 text-sm italic">
                    Sleep teams naar de gewenste poule of gebruik de knoppen.
                </p>
                <div className="flex gap-2">
                    <button
                        onClick={handleClearAssignment}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                    >
                        <Trash2 size={16} />
                        Alles Wissen
                    </button>
                    <button
                        onClick={handleAutoAssign}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-lg shadow-sm transition-all"
                    >
                        <Wand2 size={16} />
                        Auto Indelen
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 flex-1 min-h-0">

                {/* LEFT COLUMN: Unassigned Teams */}
                <div
                    className="lg:col-span-1 bg-gray-100 rounded-2xl p-4 flex flex-col h-full border-2 border-dashed border-gray-300"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, null)}
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold text-gray-700 flex items-center gap-2">
                            <Users size={20} />
                            Niet Ingedeeld
                        </h3>
                        <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded-full text-xs font-bold">
                            {unassignedTeams.length}
                        </span>
                    </div>

                    <div className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                        {unassignedTeams.length === 0 ? (
                            <div className="text-center py-8 text-gray-400 italic text-sm">
                                Alle teams zijn ingedeeld! 🎉
                            </div>
                        ) : (
                            unassignedTeams.map(team => (
                                <DraggableTeam key={team.id} team={team} onDragStart={handleDragStart} />
                            ))
                        )}
                    </div>
                </div>

                {/* RIGHT COLUMN: Poules Grid */}
                <div className="lg:col-span-3 overflow-y-auto custom-scrollbar">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {poules.length === 0 ? (
                            <div className="col-span-full text-center py-12 bg-white/50 rounded-xl">
                                <p className="text-gray-500">Maak eerst poules aan in het "Beheer" tabblad.</p>
                            </div>
                        ) : (
                            poules.map(poule => (
                                <PouleDropZone
                                    key={poule.id}
                                    poule={poule}
                                    teams={getTeamsByPoule(poule.id)}
                                    onDragOver={handleDragOver}
                                    onDrop={handleDrop}
                                    handleDragStart={handleDragStart}
                                />
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>

    )
}

const DraggableTeam = ({ team, onDragStart }) => {
    return (
        <div
            draggable
            onDragStart={(e) => onDragStart(e, team)}
            className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 cursor-grab active:cursor-grabbing hover:shadow-md hover:border-orange-300 transition-all flex items-center gap-3 group"
        >
            <GripVertical size={16} className="text-gray-300 group-hover:text-orange-400" />
            <div className="flex items-center gap-2 overflow-hidden">
                {team.logo_url && (
                    <img src={team.logo_url} alt="" className="w-6 h-6 rounded-full object-cover bg-gray-100" />
                )}
                <span className="font-medium text-gray-800 truncate text-sm">{team.name}</span>
            </div>
        </div>
    )
}

const PouleDropZone = ({ poule, teams, onDragOver, onDrop, handleDragStart }) => {
    return (
        <div
            onDragOver={onDragOver}
            onDrop={(e) => onDrop(e, poule.id)}
            className="bg-white/80 backdrop-blur-sm rounded-xl border border-white/60 shadow-lg flex flex-col min-h-[200px] transition-colors"
        >
            {/* Header */}
            <div className="p-3 border-b border-gray-100 bg-gray-50/50 rounded-t-xl flex justify-between items-center">
                <div>
                    <h4 className="font-bold text-gray-800">{poule.name}</h4>
                    <span className="text-xs text-gray-500">{poule.division?.name || 'Geen divisie'}</span>
                </div>
                <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs font-bold">
                    {teams.length}
                </span>
            </div>

            {/* Drop Area */}
            <div className="flex-1 p-3 space-y-2">
                {teams.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-gray-400 text-xs italic border-2 border-dashed border-gray-100 rounded-lg min-h-[100px]">
                        Sleep teams hierheen
                    </div>
                ) : (
                    teams.map(team => (
                        <DraggableTeam key={team.id} team={team} onDragStart={handleDragStart} />
                    ))
                )}
            </div>
        </div>
    )
}
